'use client';
import { useRef, useState } from 'react';

import { localesActions, modifyLinkIfSelected } from '@/lib/i18n/utils';
import { cn } from '@/lib/utils';
import { useClickOutside } from '@/hooks/useClickOutside';

import Button from '@/components/atoms/button';
import { Dropdown, DropdownMenu } from '@/components/atoms/dropdown';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import {
  HeaderProps,
  HeaderSBProps,
} from '@/components/atoms/layout/header/HeaderSB';
import MobileHeaderItemListSB from '@/components/atoms/layout/header/MobileHeaderItemListSB';
import { LinkButtonProps } from '@/components/atoms/linkButton';
import LinkButtonSB from '@/components/atoms/linkButton/LinkSB';
import { Overlay } from '@/components/atoms/overlay';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { ImageLinkStrapi } from '@/components/molecules/imageLink/ImageLinkStrapi';

import IconCross from '~/svg/IconCross.svg';
import IconHamburger from '~/svg/IconHamburger.svg';
import IconTranslate from '~/svg/IconTranslate.svg';

interface MobileHeaderMainSBProps {
  logo: ImageSBProps;
  logoLink?: HeaderSBProps['data']['logoLink'];
  logoAlignment?: HeaderSBProps['data']['logoAlignment'];
  firstLevels: HeaderProps['header']['firstLevels'];
  bottomSection?: HeaderSBProps['data']['extraSectionLinks'];
  showLanguages: boolean;
}

export default function MobileHeaderMainSB({
  block,
}: BlockComponentModel<MobileHeaderMainSBProps>) {
  const { logo, logoLink, firstLevels, bottomSection, showLanguages } = block;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState<string>('level-0-0');
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    ref: headerRef as React.MutableRefObject<HTMLElement>,
    callback: () => {
      setSelectedMenu('level-0-0');
      setIsOpen(false);
    },
  });

  function handleOpenToggle() {
    setIsOpen(!isOpen);
  }

  function handleMenuSelect(value: string) {
    setSelectedMenu(value);
  }

  function modifyBottomButtonProps(index: number): Partial<LinkButtonProps> {
    if (!firstLevels) return {};

    const isFirst = index === 0;
    return isFirst
      ? { variant: 'filled', color: 'primary' }
      : {
          variant: 'outlined',
          color: 'tertiary',
        };
  }

  function addLanguagesToFirstLevels(): HeaderProps['header']['firstLevels'] {
    return [
      ...(!!firstLevels ? firstLevels : []),
      {
        id: 'languages',
        level: '1',
        title: (
          <div className='flex items-center gap-2'>
            <IconTranslate /> Languages
          </div>
        ),
        secondLevels: Object.entries(localesActions()).map(([key, value]) => ({
          id: key,
          level: '2',
          title: value.text,
          link: {
            text: value.text,
            url: value.link,
          },
          customLink: modifyLinkIfSelected({
            link: value.link,
            text: value.text,
          }),
          thirdLevels: [],
        })),
      },
    ];
  }

  const modifiedFirstLevels = showLanguages
    ? addLanguagesToFirstLevels()
    : firstLevels;

  return (
    <div data-tracker-component='menu-mobile'>
      <div className='flex justify-between' ref={headerRef}>
        <div className='flex items-center'>
          {logoLink ? (
            <ImageLinkStrapi
              inner={{ classNames: { image: 'max-w-[150px] h-10' } }}
              block={{ image: logo, link: { url: logoLink, text: '' } }}
            />
          ) : (
            <ImageSB
              block={logo}
              inner={{ classNames: { image: 'max-w-[150px] h-10' } }}
            />
          )}
        </div>
        {firstLevels.length > 0 && (
          <div className='flex items-center gap-6'>
            <Dropdown detached className={cn(!isOpen && 'hidden')}>
              <DropdownMenu
                align='right'
                classNames={{
                  wrapper:
                    'bg-neutral-N99 mt-6 md:mt-8 w-[16.25rem] h-[calc(100%-3rem)] px-5 py-8 fixed',
                }}
              >
                <div className='flex h-full flex-col justify-between'>
                  <div
                    ref={ref}
                    className='flex h-full flex-col justify-between gap-4'
                  >
                    <MobileHeaderItemListSB
                      block={{
                        menuLevel:
                          modifiedFirstLevels as HeaderProps['header']['firstLevels'],
                        level: '0',
                      }}
                      containerRef={ref}
                      selectedMenu={selectedMenu}
                      onMenuSelect={handleMenuSelect}
                      currentIndex={String(0)}
                    />
                    <div className='order-1 flex flex-col gap-5'>
                      {bottomSection?.map((item, index) => (
                        <LinkButtonSB
                          key={index}
                          block={item}
                          inner={{
                            fullwidth: true,
                            onClick: () => setIsOpen(false),
                            ...modifyBottomButtonProps(index),
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </DropdownMenu>
            </Dropdown>
            <Button
              slim
              variant='text'
              color='tertiary'
              onClick={() => handleOpenToggle()}
              classNames={{ root: 'font-sans' }}
            >
              {isOpen ? (
                <div className='h-6 w-6'>
                  <IconCross />
                </div>
              ) : (
                <IconHamburger />
              )}
            </Button>
          </div>
        )}
      </div>
      {isOpen && (
        <Overlay classNames={{ root: 'left-[unset] right-[260px]' }} />
      )}
    </div>
  );
}
